import React, { useEffect, useState, FC } from "react"
import { observer } from "mobx-react-lite"
import {
  ViewStyle, FlatList, View, ImageStyle,
  TouchableOpacity, TextStyle, Appearance,
} from "react-native"
import {
  Screen,
  Text,
  ListEmpty,
  NotificationItem,
  AutoImage,
  Button,
  ListNetworkError,
} from "../../components"
import { color } from "../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { useStores } from "../../models"
import moment from "moment"
import { navigate } from "../../navigators"
import { delay } from "../../utils/delay"
import JalaAnalytic from "../../utils/jala-analytic"
import { SafeAreaView } from "react-native-safe-area-context"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const LOAD_CONTAINER: ViewStyle = {
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '18%'
}

const EMPTY_STATE: ViewStyle = {
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center'
}

const REFRESH_BUTTON: ViewStyle = {
  backgroundColor: color.primary,
  padding: 8,
  borderRadius: 5,
  alignItems: 'center',
  width: '40%'
}

const INSIDE_EMPTY_STATE: ViewStyle = {
  alignItems: 'center'
}

const IMAGE_LOAD: ImageStyle = {
  height: '30%',
  resizeMode: 'contain'
}

const LOAD_TEXT: TextStyle = {
  marginTop: 8,
  textAlign: 'center'
}

const CONTENT_LIST_CONTAINER: ViewStyle = {
  // flexGrow: 1,
  backgroundColor: color.palette.white,
  paddingBottom: 68

}

const BUTTON_CONTAINER: ViewStyle = {
  position: 'absolute',
  flexDirection: 'row',
  justifyContent: 'center',
  left: 0,
  right: 0,
  bottom: 0,
  paddingVertical: 10
}

const BUTTON_TITLE: TextStyle = {
  color: color.primary
}

const BUTTON_STYLE: ViewStyle = {
  backgroundColor: color.palette.white,
  borderRadius: 24,
  borderWidth: 1,
  paddingHorizontal: 16,
  paddingVertical: 10,
  borderColor: color.primary
}

const notificationTypes = [
  'App\\Notifications\\ReminderDailyMeasurement',
  'App\\Notifications\\ReminderDailyCurrentCycleSampling',
  'App\\Notifications\\ReminderDailyCurrentCycleFeed',
  'App\\Notifications\\InfoBiomassPredictionFarmWeekly',
  'App\\Notifications\\InfoFeedAccumulationFarmWeekly',
  'App\\Notifications\\ReminderDailyCurrentCycleHarvest',
  'App\\Notifications\\CongratulationAgeCheckpoint',
  'App\\Notifications\\ReportMonthlyFarm'
]

export const NetworkError: FC<{ colorScheme?: "light"|"dark", onPress?: () => void }> = observer(function NetworkError(props) {
  const { colorScheme, onPress } = props
  return (
    <View style={EMPTY_STATE}>
      <View style={INSIDE_EMPTY_STATE}>
        <ListEmpty />
        <TouchableOpacity style={REFRESH_BUTTON} onPress={onPress}>
          <Text preset="display3" tx="notifications.refreshPage" colorScheme={colorScheme} color={color.palette.gray800} />
        </TouchableOpacity>
      </View>
    </View>
  )
})

export const WaitingLoad: FC<{ colorScheme?: "light"|"dark", isLoading?: boolean }> = observer(function WaitingLoad(props) {
  const { isLoading, colorScheme } = props;
  const subtitle = isLoading ? 'notifications.waitingForLoad' : 'notifications.emptyNotification';
  const img = require("./waiting_load.png")
  return (
    <View style={LOAD_CONTAINER}>
      <AutoImage source={img} style={IMAGE_LOAD} />
      <Text style={LOAD_TEXT} preset="display3" tx={subtitle} colorScheme={colorScheme} color={color.palette.gray800} />
    </View>
  );
})

export const EmptyNotif: FC<{ isError?: boolean, loading?: boolean, onRefresh?: () => void }> = observer(function EmptyNotif(props) {
  const { isError, loading, onRefresh } = props
  return isError ? <ListNetworkError onRefresh={onRefresh} /> :  <WaitingLoad isLoading={loading} />
})

export const FlyingButton: FC<{ onPress?: () => void, state?: boolean }> = observer(function FlyingButton(props) {
  const { onPress, state } = props
  return (
    <View style={BUTTON_CONTAINER}>
      <Button
        testID="jala:id/read-all-button"
        tx="notifications.readAll"
        textStyle={BUTTON_TITLE}
        textPreset="title4"
        style={BUTTON_STYLE}
        onPress={onPress}
        disabled={state}
      />
    </View>
  )
})


export const FarmingNotificationScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function FarmingNotificationScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const {
    appStore,
    tokenStore: { token },
    notificationsStore, notificationsStore: { farmingNotifications, loading, error }
  } = useStores()
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [allRead, setAllRead] = useState<boolean>(false);

  useEffect(() => {
    if (token) notificationsStore.init(notificationTypes)
  }, [notificationsStore, token])

  // useEffect(() => {
  //   if (token) {
  //     notificationsStore.fetchNotifications(1, notificationTypes, false)
  //   }
  // },[token, route.name, notificationsStore])

  useEffect(() => {
    JalaAnalytic.log('Mobile app: home - notifications - cultivation');
  }, [])

  const onRefresh = () => {
    setRefreshing(true)
    notificationsStore.fetchNotifications(1, notificationTypes, false)
      .finally(() => setRefreshing(false))
  }

  const markAsRead = (item) => {
    const index = farmingNotifications.findIndex((notification) => notification.id === item.id);
    const ns = [...farmingNotifications];
    ns[index].read_at = moment.utc().format('YYYY-MM-DD HH:mm:ss');
    ns[index].unread = false;
    notificationsStore.set('notifications', [...ns])
    notificationsStore.fetchNotification(item.id).catch();
  }

  const markAllAsRead = () => {
    setAllRead(true)
    notificationsStore.set('notifPage', 1)
    notificationsStore.markNotificationsRead(notificationTypes)
      .then(() => notificationsStore.fetchNotifications(1, notificationTypes, false))
      .finally(() => setAllRead(false));
  }

  const onPress = (item) => async () => {
    JalaAnalytic.log('Mobile: Notification - Post clicked', { props: item });
    if (notificationTypes.includes(item.type)) {
      markAsRead(item);
      await appStore.set('farmId', item.data.id)
      if (appStore.farmStore) {
        appStore.farmStore.initWithLastBatchAndWaterQualityMultiMeasurements();
        await delay(1)
        navigate('App.Stack', 'Home.Stack', { screen: 'Home.Farm.Pond.List' });
      }
    }
  }

  const renderitemNotification = (item) => (
    <NotificationItem notification={item.item} onPressItem={onPress(item.item)} />
  )

  const renderListNotification = () => (
    <FlatList
      keyExtractor={(item) => (item.id)}
      data={farmingNotifications}
      renderItem={renderitemNotification}
      onRefresh={onRefresh}
      refreshing={refreshing}
      // onEndReached={() => {
      //   if (meta.current_page && meta.current_page < meta.last_page) {
      //     notificationsStore.fetchNotifications(page + 1, notificationTypes, false)
      //     notificationsStore.set('page', page + 1)
      //   }
      // }}
      // onEndReached={() => notificationsStore.getNotificationsNext(notificationTypes)}
      onEndReachedThreshold={0.1}
      // ListFooterComponent={(
      //   (meta.current_page && meta.current_page < meta.last_page) && (
      //   <View style={FOOTER_LIST}>
      //     <ActivityIndicator size={28} />
      //   </View>
      //   )
      // )}
      ListFooterComponent={<SafeAreaView edges={['bottom']} />}

      contentContainerStyle={CONTENT_LIST_CONTAINER}
    />
  )

  const unreadNotifications = farmingNotifications.filter((notification) => notification.unread);

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundColor={colorScheme === "light" ? color.background : undefined}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
    >
      {!!farmingNotifications.length && !loading && !error ? renderListNotification() : <EmptyNotif isError={error} loading={loading} onRefresh={onRefresh} />}
      {!!farmingNotifications.length && !!unreadNotifications.length && (
        <FlyingButton
          onPress={markAllAsRead}
          state={allRead}
        />
      )}
    </Screen>
  )
})
